import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, CalendarDays, Users, CheckCircle2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILES_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files`;

const HARDCODED_GALLERY = [
  { id: 'g1', path: 'hooyos-place/gallery/4cd0cfd7-2550-4a66-b40a-779ef5f6abe6.jpeg', media_type: 'photo' },
  { id: 'g2', path: 'hooyos-place/gallery/ea772e4f-6f4b-4a0e-b6ec-2f2eb6b4b48a.jpeg', media_type: 'photo' },
  { id: 'g3', path: 'hooyos-place/gallery/48857230-01ca-4688-b472-793fc3897522.jpeg', media_type: 'photo' },
  { id: 'g4', path: 'hooyos-place/gallery/f927e7f9-29b1-442f-9d7c-cd79ee5cfeae.jpeg', media_type: 'photo' },
  { id: 'g5', path: 'hooyos-place/gallery/c5ece1d5-ae17-411b-a820-a935cf2ff02a.jpeg', media_type: 'photo' },
  { id: 'g6', path: 'hooyos-place/gallery/acf032e3-697a-4ab8-9992-30c4c6c81d14.jpeg', media_type: 'photo' },
  { id: 'g7', path: 'hooyos-place/gallery/0f5fdb2f-f7d8-4600-a78b-842e323ad3fd.jpeg', media_type: 'photo' },
];

const ScheduleTour = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [formData, setFormData] = useState({
    parent_name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    num_children: 1,
    child_ages: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const photos = HARDCODED_GALLERY.filter(i => i.media_type === 'photo');
  const videos = HARDCODED_GALLERY.filter(i => i.media_type === 'video');
  const lightboxPhotos = photos;
  const lightboxNav = (dir) => {
    if (lightbox === null) return;
    const idx = lightboxPhotos.findIndex(p => p.id === lightbox.id);
    const next = (idx + dir + lightboxPhotos.length) % lightboxPhotos.length;
    setLightbox(lightboxPhotos[next]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/tour-requests`, formData);
      toast.success('Tour Request Submitted!', { description: "We'll contact you shortly to confirm your tour." });
      setFormData({ parent_name: '', email: '', phone: '', preferred_date: '', preferred_time: '', num_children: 1, child_ages: '', message: '' });
      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      toast.error('Something went wrong', { description: 'Please try again or call us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  const timeSlots = [
    { value: 'morning-9', label: '9:00 AM' },
    { value: 'morning-10', label: '10:00 AM' },
    { value: 'morning-11', label: '11:00 AM' },
    { value: 'afternoon-1', label: '1:00 PM' },
    { value: 'afternoon-2', label: '2:00 PM' },
    { value: 'afternoon-3', label: '3:00 PM' },
  ];

  return (
    <div className="min-h-screen bg-background py-12 px-4" data-testid="schedule-tour-page">
      <div className="container mx-auto max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 rounded-full hover:bg-primary/8 text-foreground/70 hover:text-foreground"
            data-testid="tour-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <Link to="/">
            <div className="font-fredoka font-bold text-lg">
              <span className="text-primary">Hoo</span>
              <span className="text-secondary">yo's</span>
              <span className="text-accent"> Place</span>
            </div>
          </Link>
        </div>

        {/* Gallery Section - moved above form */}

        {/* Scrolling Gallery */}
        {(photos.length > 0 || videos.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="text-center mb-5">
              <h2 className="text-2xl font-fredoka font-bold text-foreground" data-testid="tour-gallery-heading">Take a Peek Inside</h2>
              <p className="text-muted-foreground text-sm mt-1">See our warm, welcoming learning environment</p>
            </div>

            {/* Videos */}
            {videos.length > 0 && (
              <div className="max-w-2xl mx-auto mb-5 space-y-4">
                {videos.map((item) => (
                  <div key={item.id} className="rounded-2xl overflow-hidden shadow-elevated" data-testid={`tour-video-${item.id}`}>
                    <video
                      src={`${FILES_BASE}/${item.path}`}
                      controls
                      className="w-full aspect-video bg-black"
                      preload="metadata"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Auto-scrolling photo marquee */}
            {photos.length > 0 && (
              <div className="overflow-hidden" data-testid="tour-photo-marquee">
                <style>{`
                  @keyframes marqueeScroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                  }
                  .marquee-track {
                    display: flex;
                    gap: 16px;
                    width: max-content;
                    animation: marqueeScroll ${Math.max(photos.length * 4, 20)}s linear infinite;
                  }
                  .marquee-track:hover {
                    animation-play-state: paused;
                  }
                `}</style>
                <div className="marquee-track">
                  {[...photos, ...photos].map((item, idx) => (
                    <div
                      key={`${item.id}-${idx}`}
                      className="flex-shrink-0 w-64 h-64 md:w-72 md:h-72 rounded-2xl overflow-hidden cursor-pointer shadow-soft hover:shadow-elevated hover:scale-105 transition-transform"
                      onClick={() => setLightbox(item)}
                      data-testid={idx < photos.length ? `tour-photo-${item.id}` : undefined}
                    >
                      <img
                        src={`${FILES_BASE}/${item.path}`}
                        alt="Our space"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Lightbox */}
        <AnimatePresence>
          {lightbox && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              onClick={() => setLightbox(null)}
              data-testid="tour-lightbox"
            >
              <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white/70 hover:text-white p-2" data-testid="tour-lightbox-close">
                <X className="w-6 h-6" />
              </button>
              {lightboxPhotos.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); lightboxNav(-1); }} className="absolute left-4 text-white/70 hover:text-white p-2" data-testid="tour-lightbox-prev">
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); lightboxNav(1); }} className="absolute right-4 text-white/70 hover:text-white p-2" data-testid="tour-lightbox-next">
                    <ChevronRight className="w-8 h-8" />
                  </button>
                </>
              )}
              <img
                src={`${FILES_BASE}/${lightbox.path}`}
                alt="Gallery"
                className="max-w-full max-h-[85vh] rounded-xl object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-[2.5rem] shadow-elevated border border-border/40 overflow-hidden"
        >
          {/* Top Banner */}
          <div className="bg-secondary/8 p-8 text-center space-y-4 border-b border-border/30">
            <div className="flex justify-center gap-4">
              <div className="w-14 h-14 rounded-full bg-secondary/12 flex items-center justify-center">
                <CalendarDays className="w-7 h-7 text-secondary" />
              </div>
              <div className="w-14 h-14 rounded-full bg-accent/12 flex items-center justify-center">
                <Users className="w-7 h-7 text-accent" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-fredoka font-bold text-foreground" data-testid="tour-heading">
              Schedule a Tour
            </h1>
            <p className="text-muted-foreground text-sm md:text-base max-w-md mx-auto">
              Come see our warm, welcoming space in person! Pick a date and time that works for you and we'll have everything ready.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Parent Info */}
            <div className="space-y-5">
              <h3 className="text-lg font-fredoka font-semibold text-foreground flex items-center gap-2">
                <Users className="w-5 h-5 text-secondary" />
                Your Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="parent_name">Full Name *</Label>
                  <Input id="parent_name" name="parent_name" placeholder="Jane Doe" required value={formData.parent_name} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-secondary rounded-2xl h-12" data-testid="tour-parent-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input id="phone" name="phone" type="tel" placeholder="(503) 555-1234" required value={formData.phone} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-secondary rounded-2xl h-12" data-testid="tour-phone" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input id="email" name="email" type="email" placeholder="parent@example.com" required value={formData.email} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-secondary rounded-2xl h-12" data-testid="tour-email" />
              </div>
            </div>

            {/* Tour Details */}
            <div className="space-y-5">
              <h3 className="text-lg font-fredoka font-semibold text-foreground flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-secondary" />
                Tour Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="preferred_date">Preferred Date *</Label>
                  <Input id="preferred_date" name="preferred_date" type="date" required value={formData.preferred_date} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-secondary rounded-2xl h-12" data-testid="tour-date" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_time">Preferred Time *</Label>
                  <Select value={formData.preferred_time} onValueChange={(value) => setFormData((prev) => ({ ...prev, preferred_time: value }))}>
                    <SelectTrigger className="border-2 border-border/60 focus:border-secondary rounded-2xl h-12" data-testid="tour-time-select">
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot.value} value={slot.value}>{slot.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="num_children">Number of Children</Label>
                  <Input id="num_children" name="num_children" type="number" min="1" max="10" value={formData.num_children} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-secondary rounded-2xl h-12" data-testid="tour-num-children" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="child_ages">Child Age(s)</Label>
                  <Input id="child_ages" name="child_ages" placeholder="e.g. 3 and 5" value={formData.child_ages} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-secondary rounded-2xl h-12" data-testid="tour-child-ages" />
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="space-y-2">
              <Label htmlFor="message">Questions or Special Requests</Label>
              <Textarea id="message" name="message" placeholder="Anything you'd like us to know before your visit?" rows={3} value={formData.message} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-secondary rounded-2xl resize-none" data-testid="tour-message" />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full py-6 text-lg font-bold shadow-soft hover:shadow-elevated transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              data-testid="tour-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Request a Tour'}
            </Button>

            {/* Confirmation note */}
            <div className="bg-success/8 rounded-2xl p-4 flex items-center justify-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
              <p className="text-sm text-muted-foreground text-center">
                We'll confirm your tour within 24 hours via phone or email. Walk-ins are also welcome during business hours!
              </p>
            </div>

            {/* Contact info */}
            <div className="text-center text-muted-foreground space-y-1 pt-2">
              <p className="text-sm">
                Prefer to call? Reach us at <a href="tel:971-867-0536" className="font-bold text-primary hover:underline">971-867-0536</a>
              </p>
              <p className="text-sm">Mon-Fri: 7:00 AM - 8:00 PM</p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ScheduleTour;
